/**
 * @fileoverview Dismissable success banner of the Capabilities page.
 * @description The green strip confirming a save, with a close button — same shape as User
 * Management's flash message.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

interface CapabilitiesFlashProps {
  message: string
  onDismiss: () => void
}

/**
 * @component
 * @param {CapabilitiesFlashProps} props - The message and its dismiss handler.
 * @param {string} props.message - What to say.
 * @param {() => void} props.onDismiss - Called when the close button is clicked.
 */
function CapabilitiesFlash({ message, onDismiss }: CapabilitiesFlashProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid="capabilities-flash"
      className="flex items-center gap-2.5 rounded-lg border border-success/30 bg-success-bg px-3.5 py-2.5"
    >
      <span
        className="flex-1 text-[13px] font-medium text-success"
        data-testid="capabilities-flash-message"
      >
        {message}
      </span>
      <button
        type="button"
        onClick={onDismiss}
        data-testid="capabilities-flash-dismiss-button"
        aria-label={t('capabilities.flash.dismiss')}
        className="shrink-0 cursor-pointer text-success"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
        </svg>
      </button>
    </div>
  )
}

export default CapabilitiesFlash
